if (!customElements.get('collection-catalog')) {
  customElements.define(
    'collection-catalog',
    class CollectionCatalog extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.clearUrl = this.dataset.clearUrl;
        this.desktopQuery = window.matchMedia(
          (window.theme && theme.mediaQueries && theme.mediaQueries.md) || '(min-width: 768px)'
        );

        this.form = this.querySelector('[data-cc-form]');
        this.filters = this.querySelector('[data-cc-filters]');
        this.scrim = this.querySelector('[data-cc-scrim]');
        this.results = this.querySelector('[data-cc-results]');
        this.countOutput = this.querySelector('[data-cc-count]');
        this.sortGroup = this.querySelector('[data-cc-sort]');
        this.searchInput = this.querySelector('[data-cc-search]');
        this.searchEmpty = this.querySelector('[data-cc-search-empty]');

        this.groups = Array.from(this.querySelectorAll('[data-cc-group]'));
        this.filterTimer = null;
        this.searchTimer = null;
        this.abortController = null;
        this.revealQueue = [];
        this.previousBodyOverflow = '';

        this.handleClick = this.handleClick.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleSearchInput = this.handleSearchInput.bind(this);
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handlePopState = this.handlePopState.bind(this);
        this.handleViewportChange = this.handleViewportChange.bind(this);
        this.handleReveal = this.handleReveal.bind(this);

        this.addEventListener('click', this.handleClick);
        this.addEventListener('change', this.handleChange);
        document.addEventListener('click', this.handleOutsideClick);
        document.addEventListener('keydown', this.handleKeydown);
        window.addEventListener('popstate', this.handlePopState);

        if (this.form) this.form.addEventListener('submit', this.handleSubmit);
        if (this.searchInput) this.searchInput.addEventListener('input', this.handleSearchInput);

        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', this.handleViewportChange);
          window.visualViewport.addEventListener('scroll', this.handleViewportChange);
        }

        if ('IntersectionObserver' in window) {
          this.revealObserver = new IntersectionObserver(this.handleReveal, {
            rootMargin: '0px 0px -40px 0px',
            threshold: 0.05
          });
        }

        this.observeCells(this.cells(), true);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('change', this.handleChange);
        document.removeEventListener('click', this.handleOutsideClick);
        document.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('popstate', this.handlePopState);

        if (this.form) this.form.removeEventListener('submit', this.handleSubmit);
        if (this.searchInput) this.searchInput.removeEventListener('input', this.handleSearchInput);

        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this.handleViewportChange);
          window.visualViewport.removeEventListener('scroll', this.handleViewportChange);
        }

        if (this.revealObserver) this.revealObserver.disconnect();
        if (this.abortController) this.abortController.abort();
        clearTimeout(this.filterTimer);
        clearTimeout(this.searchTimer);
        this.releaseBodyScroll();
      }

      cells() {
        return Array.from(this.querySelectorAll('[data-cc-cell]'));
      }

      handleClick(evt) {
        const toggle = evt.target.closest('[data-cc-toggle]');
        if (toggle && this.contains(toggle)) {
          evt.preventDefault();
          this.toggleGroup(toggle.closest('[data-cc-group]'));
          return;
        }

        const sortToggle = evt.target.closest('[data-cc-sort-toggle]');
        if (sortToggle && this.contains(sortToggle)) {
          evt.preventDefault();
          this.toggleSort();
          return;
        }

        const sortLink = evt.target.closest('[data-cc-sort-link]');
        if (sortLink && this.contains(sortLink)) {
          evt.preventDefault();
          this.toggleSort(false);
          this.selectSort(sortLink);
          return;
        }

        if (evt.target.closest('[data-cc-drawer-open]')) {
          evt.preventDefault();
          this.setDrawer(true);
          return;
        }

        if (evt.target.closest('[data-cc-drawer-close]') || evt.target.closest('[data-cc-scrim]')) {
          evt.preventDefault();
          this.setDrawer(false);
          return;
        }

        if (evt.target.closest('[data-cc-apply]')) {
          evt.preventDefault();
          this.setDrawer(false);
          this.submitFilters();
          return;
        }

        if (evt.target.closest('[data-cc-clear]')) {
          evt.preventDefault();
          this.setDrawer(false);
          if (this.clearUrl) this.requestUrl(this.clearUrl);
          return;
        }
      }

      handleChange(evt) {
        if (!this.form || !this.form.contains(evt.target)) return;
        if (!evt.target.matches('input, select')) return;
        if (!this.desktopQuery.matches) return;

        clearTimeout(this.filterTimer);
        this.filterTimer = setTimeout(() => this.submitFilters(), 450);
      }

      handleSubmit(evt) {
        evt.preventDefault();
        this.submitFilters();
      }

      handleKeydown(evt) {
        if (evt.key !== 'Escape') return;

        if (this.filters && this.filters.classList.contains('cc-catalog__filters--open')) {
          this.setDrawer(false);
          return;
        }
        this.closeGroups();
        this.toggleSort(false);
      }

      handleOutsideClick(evt) {
        if (this.contains(evt.target)) return;
        this.closeGroups();
        this.toggleSort(false);
      }

      toggleGroup(group, force) {
        if (!group) return;

        const open = force === undefined ? !group.classList.contains('cc-catalog__group--open') : force;
        if (open && this.desktopQuery.matches) this.closeGroups(group);

        group.classList.toggle('cc-catalog__group--open', open);
        const toggle = group.querySelector('[data-cc-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', String(open));

        if (open && this.searchInput && group.contains(this.searchInput) && !this.desktopQuery.matches) {
          this.searchInput.focus({ preventScroll: true });
        }
      }

      closeGroups(except) {
        this.groups.forEach((group) => {
          if (group !== except) this.toggleGroup(group, false);
        });
      }

      toggleSort(force) {
        if (!this.sortGroup) return;

        const open = force === undefined ? !this.sortGroup.classList.contains('cc-catalog__sort--open') : force;
        if (open) this.closeGroups();

        this.sortGroup.classList.toggle('cc-catalog__sort--open', open);
        const toggle = this.sortGroup.querySelector('[data-cc-sort-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', String(open));
      }

      setDrawer(open) {
        if (!this.filters) return;

        this.filters.classList.toggle('cc-catalog__filters--open', open);
        this.filters.setAttribute('aria-hidden', String(!open));
        if (this.scrim) this.scrim.classList.toggle('cc-catalog__scrim--open', open);

        const opener = this.querySelector('[data-cc-drawer-open]');
        if (opener) opener.setAttribute('aria-expanded', String(open));

        if (open) {
          this.previousBodyOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
        } else {
          this.releaseBodyScroll();
        }
      }

      releaseBodyScroll() {
        if (document.body.style.overflow === 'hidden') {
          document.body.style.overflow = this.previousBodyOverflow;
        }
      }

      handleViewportChange() {
        const viewport = window.visualViewport;
        if (!viewport) return;

        const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
        this.style.setProperty('--cc-keyboard-inset', `${Math.round(inset)}px`);
      }

      handleSearchInput() {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.filterOptions(), 120);
      }

      filterOptions() {
        if (!this.searchInput) return;

        const term = this.searchInput.value.trim().toLowerCase();
        const options = Array.from(this.querySelectorAll('[data-cc-searchable]'));
        let visible = 0;

        options.forEach((option) => {
          const match = !term || option.dataset.ccSearchable.indexOf(term) >= 0;
          option.classList.toggle('cc-catalog__option--filtered', !match);
          if (match) {
            option.style.setProperty('--cc-i', visible);
            visible += 1;
          }
        });

        if (this.searchEmpty) this.searchEmpty.hidden = visible > 0;
      }

      selectSort(link) {
        const field = this.form && this.form.querySelector('[data-cc-sort-field]');
        if (!field) {
          this.requestUrl(link.href);
          return;
        }

        field.value = link.dataset.ccValue;
        const toggle = this.querySelector('[data-cc-sort-toggle] span');
        if (toggle) toggle.textContent = link.textContent.trim();

        this.querySelectorAll('[data-cc-sort-link]').forEach((item) => {
          item.classList.toggle('cc-catalog__sort-link--active', item === link);
        });

        this.submitFilters();
      }

      submitFilters() {
        if (!this.form) return;
        this.requestUrl(this.buildUrl());
      }

      buildUrl() {
        const params = new URLSearchParams();

        Array.from(this.form.elements).forEach((field) => {
          if (!field.name || field.disabled) return;
          if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
          if (field.value === '') return;
          params.append(field.name, field.value);
        });

        const query = params.toString();
        return query ? `${window.location.pathname}?${query}` : window.location.pathname;
      }

      handlePopState() {
        this.requestUrl(window.location.href, { push: false });
      }

      async requestUrl(url, options) {
        const settings = Object.assign({ push: true, append: false }, options);

        if (settings.push) {
          const absolute = url.startsWith('/') ? `${window.location.origin}${url}` : url;
          window.history.pushState({ path: absolute }, '', absolute);
        }

        if (this.results) this.results.classList.add('cc-catalog__results--loading');

        const doc = await this.fetchDocument(url);
        if (!doc) {
          if (this.results) this.results.classList.remove('cc-catalog__results--loading');
          return;
        }

        this.replaceRegion('[data-cc-results]', doc);
        this.replaceRegion('[data-cc-count]', doc);
        this.syncFormFrom(doc);

        this.results = this.querySelector('[data-cc-results]');
        this.countOutput = this.querySelector('[data-cc-count]');
        if (this.results) this.results.classList.remove('cc-catalog__results--loading');

        this.observeCells(this.cells());
        this.scrollToTop();
      }

      async fetchDocument(url) {
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        const separator = url.indexOf('?') >= 0 ? '&' : '?';
        const fetchUrl = `${url}${separator}section_id=${this.sectionId}`;

        try {
          const response = await fetch(fetchUrl, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`Collection catalog request failed: ${response.status}`);

          const markup = await response.text();
          return new DOMParser().parseFromString(markup, 'text/html');
        } catch (error) {
          if (error.name !== 'AbortError') console.error(error);
          return null;
        }
      }

      replaceRegion(selector, doc) {
        const current = this.querySelector(selector);
        const next = doc.querySelector(selector);
        if (current && next) current.innerHTML = next.innerHTML;
      }

      syncFormFrom(doc) {
        if (!this.form) return;

        const next = doc.querySelector('[data-cc-form]');
        if (!next) return;

        Array.from(this.form.elements).forEach((field) => {
          if (!field.name) return;
          const source = next.querySelector(`[name="${CSS.escape(field.name)}"][value="${CSS.escape(field.value)}"]`);

          if (field.type === 'checkbox' || field.type === 'radio') {
            field.checked = Boolean(source && source.checked);
          }
        });

        const sortField = this.form.querySelector('[data-cc-sort-field]');
        const nextSort = next.querySelector('[data-cc-sort-field]');
        if (sortField && nextSort) sortField.value = nextSort.value;
      }

      observeCells(cells, keepRevealed = false) {
        if (!cells || !cells.length) return;

        if (!this.revealObserver) {
          cells.forEach((cell) => cell.classList.add('cc-catalog__cell--in'));
          return;
        }

        cells.forEach((cell) => {
          if (keepRevealed && cell.classList.contains('cc-catalog__cell--in')) return;
          cell.classList.remove('cc-catalog__cell--in');
          cell.style.transitionDelay = '';
          this.revealObserver.observe(cell);
        });
      }

      handleReveal(entries) {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => entry.target)
          .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

        visible.forEach((cell, index) => {
          cell.style.transitionDelay = `${Math.min(index * 60, 320)}ms`;
          cell.classList.add('cc-catalog__cell--in');
          this.revealObserver.unobserve(cell);
        });
      }

      scrollToTop() {
        const anchor = this.querySelector('[data-cc-anchor]');
        if (!anchor) return;

        const header =
          window.theme && typeof theme.stickyHeaderHeight === 'function' ? theme.stickyHeaderHeight() : 0;
        const top = anchor.getBoundingClientRect().top + window.scrollY - header - 20;
        if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
      }
    }
  );
}
